import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Button,
  Modal,
  Row,
  Col,
  Divider,
  Select,
  message,
  Form,
  TimePicker,
  Typography,
  Badge,
  Dropdown,
  Spin,
  Tooltip,
  Menu,
  Popconfirm,
} from "antd";
import StrictDatePicker from "./StrictDatePicker";
import { RemoveFilters } from "./RemoveFilters";
import {
  getCrewAll,
  deleteCrew,
  getCrew,
  updateCrewAndEmployeeDetails,
  getCrewUser,
  CrewScheduleWorkmen,
  copyCrews,
  refreshData
} from "../../services/crew";
import MainStepper from "./MainStepper";
import UnassignDashboard from "./UnAssignDashboard";
import dayjs from "dayjs";
import {
  MoreOutlined,
  CopyOutlined,
  SwapOutlined,
  DeleteOutlined,
  FilterOutlined,
  CloseOutlined,
  EditOutlined,
  DownloadOutlined,
  DownOutlined,
  ReloadOutlined,
  RedoOutlined
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import AiCrewEntry from "./crewCall/AiCrewEntry";
import useAuth from "../../hooks/useAuth";
import SmartTable from "./SmartTable";
import HOC from "./HOC";
import "./Style.css";
import { useNavigate } from "react-router-dom";
import { getDetails } from "../../services/auth";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ScheduleConflictHandler from "./ScheduleConflictHandler";
import { initializeCrewDB, sortCrews } from './sortingUtils';
const { Title } = Typography;
const { Option } = Select;
const FILTER_STORAGE_KEY = "crewAppliedFilters";
const FILTER_FORM_STORAGE_KEY = "crewFilterFormValues";
const SHARED_SELECTED_DATE_STORAGE_KEY = "sharedSelectedDate";
const CREW_VIEW_STORAGE_KEY = "crewViewSelection";
const baseHeaderStyle = {
  backgroundColor: "#EAEEFF",
  color: "black",
  borderRight: "1px solid #d9d9d9",
  fontWeight: "bold",
};
const formatTime = (t) => {
  if (!t) return "";
  if (dayjs.isDayjs(t)) return t.format("HH:mm");
  if (typeof t === "string" && t.includes("T")) {
    const parsed = dayjs(t);
    if (parsed.isValid()) return parsed.format("HH:mm");
  }
  if (typeof t === "string" && /^\d{2}:\d{2}$/.test(t)) return t;
  const parsed = dayjs(t);
  if (parsed.isValid()) return parsed.format("HH:mm");
  return t;
};
const getInitialSelectedDate = () => {
  try {
    const stored = sessionStorage.getItem(SHARED_SELECTED_DATE_STORAGE_KEY);
    if (stored) {
      const parsed = dayjs(stored);
      if (parsed.isValid()) {
        return parsed.startOf("day");
      }
    }
  } catch (err) {
    console.error("Failed to parse stored date", err);
  }
  return dayjs().add(1, "day").startOf("day");
};
function CrewDashboard() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isUnassignModalOpen, setIsUnassignModalOpen] = useState(false);
  const [showFilterDot, setShowFilterDot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [crews, setCrews] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedDate, setSelectedDate] = useState(getInitialSelectedDate());
  const [ignoreDateFilter, setIgnoreDateFilter] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [workmen, setWorkmen] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [scheduledEmployees, setScheduledEmployees] = useState([]);
  const [crewClickedDate, setCrewClickedDate] = useState(null);
  const [copyBuffer, setCopyBuffer] = useState([]);
  const [transferOwner, setTransferOwner] = useState(null);
  const [transferOwnerName, setTransferOwnerName] = useState(null);
  const [conflictResponse, setConflictResponse] = useState(null);
  const [theCrew, setTheCrew] = useState(null);
  const [filterForm] = Form.useForm();
  const [copyForm] = Form.useForm();
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const navigate = useNavigate();
  const { userConfig, tokenInfo } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [permissionsGeneralAccess, setPermissionsGeneralAccess] = useState([]);
  const [hasHyperfindAccess, setHasHyperfindAccess] = useState(false);
  const [hasDownloadAccess, setHasDownloadAccess] = useState(false);
  const [backDatedEntry, setBackDatedEntry] = useState(false);
  const [tablePageSize, setTablePageSize] = useState(100);
  const [tableCurrentPage, setTableCurrentPage] = useState(1);

  useEffect(() => {
    const key = userConfig?.userid ? `employee-table-page-size-${userConfig.userid}` : null;
    if (!key || typeof window === "undefined") return;
    const stored = window.localStorage.getItem(key);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    if (Number.isFinite(parsed) && parsed > 0) {
      setTablePageSize(parsed);
    }
  });
  const [visibleCrews, setVisibleCrews] = useState([]); // Track filtered/visible rows
  const [crewView, setCrewView] = useState(() => {
    try {
      const stored = sessionStorage.getItem(CREW_VIEW_STORAGE_KEY);
      return stored === 'allCrews' ? 'allCrews' : 'myCrews';
    } catch {
      return 'myCrews';
    }
  });
  const [canViewCrew, setCanViewCrew] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [templatePermissions, setTemplatePermissions] = useState(null);
  const [scheduleOverridePermission, setScheduleOverridePermission] = useState(null);
  const stepperResetRef = useRef(null);
  const [startTimeOpen, setStartTimeOpen] = useState(false);
  const [crewDB, setCrewDB] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: "crewName", order: "ascend" });
  const originalCrewData = useRef([]);
  const isSortingRef = useRef(false);
  const tableFiltersRef = useRef(null);
  const scheduleStatusFiltersRef = useRef(null);
  const getAddonPermission = () => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find((p) => p?.feature === "Add-ons");
  };
const getCrewManagementPermission = () => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find((p) => p?.feature === "Crew & Schedule Management");
  };
  const getCrewTemplateManagementPermission = () => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find((p) => p?.feature === "Crew Template Management");
  };
const addonPerm = getAddonPermission();
  const copyCrew = addonPerm?.subFeatures?.find(
    (sf) => sf.name === "Copy Crew"
  );
  const transferCrew = addonPerm?.subFeatures?.find(
    (sf) => sf.name === "Transfer Crew"
  );
  const filterCrew = addonPerm?.subFeatures?.find(
    (sf) => sf.name === "Filters (Crew, Employee, General)"
  );
  const copyCrewAceess = copyCrew?.access == true;
  const transferCrewAcess = transferCrew?.access == true;
  const filterCrewAcess = filterCrew?.access == true;
  const CrewTemplateManagementAcess = getCrewTemplateManagementPermission()?.access == true;
  const crewPerm = getCrewManagementPermission();
  const CrewAcees = crewPerm?.subFeatures?.find(
    (sf) => sf.name === "Crew Management"
  );
  const canCreateCrew = !!(CrewAcees && crewPerm.access && CrewAcees.create && CrewAcees.create !== 'None');
  const crewViewAccess = CrewAcees?.view;
  const canSwitchCrewView = crewViewAccess === "All";
const canDeleteRow = (row) => {
    if (CrewAcees?.delete === "None") return false;

    if (CrewAcees?.delete === "All") return true;

    if (CrewAcees?.delete === "Myself") {
      return userConfig.userid === row.crewOwnerId;
    }
 return false;
  };

const parseTimeInputValue = (rawValue) => {
    if (!rawValue) return null;
    const value = String(rawValue).trim();
    const match = value.match(/^\s*(\d{1,2}):(\d{2})\s*$/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
  };
const updateApplyDisabledState = () => {
    try {
      const values = filterForm.getFieldsValue();
      const isEmpty = Object.values(values).every((v) => {
        if (v === undefined || v === null) return true;
        if (Array.isArray(v)) return v.length === 0;
        if (typeof v === "string") return v.trim() === "";
        if (dayjs.isDayjs && dayjs.isDayjs(v)) return false;
        if (typeof v === "object") return Object.keys(v).length === 0;
        return false;
      });
      setIsApplyDisabled(isEmpty);
    } catch (e) {
      setIsApplyDisabled(true);
    }
  };
const handleTimeInput = (e, fieldName, setOpenFn) => {
    const input = e.target;
    const key = e.key;
    if (!/^[0-9]$/.test(key)) return;
   e.preventDefault();
  let currentValue = input.value.replace(/[^0-9]/g, '');
    if (!currentValue || (input.selectionStart === 0 && input.selectionEnd === input.value.length)) {
      currentValue = '';
    }
   const digit = key;
   if (currentValue.length === 0 && /^[3-9]$/.test(digit)) {
      const formatted = `0${digit}:`;
      input.value = formatted;
      setTimeout(() => input.setSelectionRange(3, 3), 0);
      return;
    }
   if (currentValue.length === 0 && /^[0-2]$/.test(digit)) {
      input.value = digit;
      return;
    }
   if (currentValue.length === 1 && /^[0-1]$/.test(currentValue)) {
      const formatted = `${currentValue}${digit}:`;
      input.value = formatted;
      setTimeout(() => input.setSelectionRange(3, 3), 0);
      return;
    }
    if (currentValue.length === 1 && currentValue === '2') {
      if (/^[4-9]$/.test(digit)) {
        input.value = '23:';
        setTimeout(() => input.setSelectionRange(3, 3), 0);
      } else if (/^[0-3]$/.test(digit)) {
        const formatted = `2${digit}:`;
        input.value = formatted;
        setTimeout(() => input.setSelectionRange(3, 3), 0);
      }
      return;
    }
  if (currentValue.length >= 2 && input.value.includes(':')) {
      const parts = input.value.split(':');
      const hourPart = parts[0];
      let minutePart = (parts[1] || '').replace(/[^0-9]/g, '');
    if (minutePart.length === 0 && /^[6-9]$/.test(digit)) {
        message.error({ content: "Invalid Entry: Minutes cannot exceed 59", key: "invalid-time", duration: 1.5 });
        return;
      }
     if (minutePart.length === 0 && /^[0-5]$/.test(digit)) {
        input.value = `${hourPart}:${digit}`;
        return;
      }
     if (minutePart.length === 1) {
        const firstMinuteDigit = parseInt(minutePart[0]);
        const secondMinuteDigit = parseInt(digit);
        const totalMinutes = firstMinuteDigit * 10 + secondMinuteDigit;
     if (totalMinutes > 59) {
          message.error({ content: "Invalid Entry: Minutes cannot exceed 59", key: "invalid-time", duration: 1.5 });
          return;
        }
    input.value = `${hourPart}:${minutePart}${digit}`;
        setTimeout(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        }, 0);
        setOpenFn?.(false);
      }
    }
  };
const totalNeeded = useMemo(
    () => crews.reduce((sum, c) => sum + (Number(c.employeesNeeded) || 0), 0),
    [crews]
  );
  const totalScheduled = useMemo(
    () => crews.reduce((sum, c) => sum + (Number(c.employeesScheduled) || 0), 0),
    [crews]
  );
  const totalDiff = totalScheduled - totalNeeded;
  const crewsWithDifference = useMemo(() => {
    return crews.map(crew => ({
      ...crew,
      difference: (Number(crew.employeesScheduled) || 0) - (Number(crew.employeesNeeded) || 0)
    }));
  }, [crews]);
const handleSort = async (field, order) => {
if (!order) {
      if (originalCrewData.current.length > 0) {
        isSortingRef.current = true;
        setCrews([...originalCrewData.current]);
        setSortConfig({ field: null, order: null });
      }
      return;
    }
isSortingRef.current = true;
    const sortedData = await sortCrews(originalCrewData.current, field, order, setLoading);
    setCrews(sortedData);
    setSortConfig({ field, order });
  };
const applyDefaultCrewSort = async (crewList) => {
    if (!Array.isArray(crewList) || crewList.length === 0) return [];
    const sortedList = await sortCrews(crewList, "crewName", "ascend", setLoading);
    setSortConfig({ field: "crewName", order: "ascend" });
    return sortedList;
  };
const effectiveViewTypeForHoc = (crewViewAccess === "Myself" || crewViewAccess === "None") ? 'myCrews' : crewView;
  const crewCol = HOC("crew", {
    date: selectedDate,
    onSort: handleSort,
    sortConfig,
    filterUserId: effectiveViewTypeForHoc === 'myCrews' ? userConfig?.userid : undefined
  });
  const crewColumns = (typeof crewCol === 'function' ? crewCol() : crewCol).map(col => ({
    ...col,
    onHeaderCell: () => ({
      style: baseHeaderStyle,
    }),
  }));
const getEmployeeExcelColumns = () => {
    return crewColumns
      .filter(col => col?.key && col?.title)
      .map(col => ({
        header: col.title,
        key: col.key,
      }));
  };
const fetchEmployeeList = async () => {
    try {
     const response = await getCrewUser();
      setWorkmen(response?.data);
    } catch (error) {
      console.error('Error fetching workmen:', error);
    } finally {
      // setLoading(false);
    }
  };
  useEffect(() => {
    if (crewsWithDifference && crewsWithDifference.length > 0) {
      if (!isSortingRef.current) {
        originalCrewData.current = JSON.parse(JSON.stringify(crewsWithDifference));
      } else {
        isSortingRef.current = false;
      }
     const db = initializeCrewDB(crewsWithDifference);
      setCrewDB(db);
    }
  }, [crewsWithDifference]);
useEffect(() => {
    if (!permissionsLoaded) return;
    if (canViewCrew) fetchCrewsForDate(selectedDate);
    else {
      setCrews([]);
      setShowFilterDot(false);
      setAppliedFilters(null);
      message.error("You do not have permission to view crews.");
    }
    setSearchText("");
  }, [canViewCrew, permissionsLoaded, selectedDate]);

  useEffect(() => {
    getRolePermissionDetails();
  }, [])
const getRolePermissionDetails = () => {
    getDetails(userConfig.orgId)
      .then((response) => {
        const fetchRole = response.data.find((role) =>
          role?.selectedRole?.toLowerCase().trim() === userConfig.role?.toLowerCase().trim() &&
          role?.selectedRoleType?.toLowerCase().trim() === userConfig.roleName?.selectedRoleType?.toLowerCase().trim()
        );
        const crewPermissions = fetchRole.permissions.cap.crewScheduling;
        setPermissions(crewPermissions);
        setPermissionsGeneralAccess(fetchRole.permissions.cap.generalAccess);
        const crewPerm = crewPermissions?.find((p) => p?.feature === "Crew & Schedule Management");
        const templatePermissions = crewPermissions?.find((p) => p?.feature === "Crew Template Management");
        const scheduleOverridePermissions = crewPermissions?.find((p) => p?.feature === "Schedule Override");
        const CrewAcees = crewPerm?.subFeatures?.find((sf) => sf.name === "Crew Management");
        const canView = !!(CrewAcees && crewPerm?.access && CrewAcees.view && CrewAcees.view !== 'None');
        setCanViewCrew(canView);
        setTemplatePermissions(templatePermissions);
        setScheduleOverridePermission(scheduleOverridePermissions);
        if (CrewAcees?.view === "Myself" || CrewAcees?.view === "None") {
          setCrewView('myCrews');
          try {
            sessionStorage.setItem(CREW_VIEW_STORAGE_KEY, 'myCrews');
          } catch (e) {
          }
        }
     setPermissionsLoaded(true);
      })
      .catch((err) => {
        console.error(err);
        setPermissionsLoaded(true);
      })
  }
useEffect(() => {
    const hyperfindAccess =
      permissionsGeneralAccess?.some(
        perm =>
          perm.feature === "Hyperfind Access" &&
          perm.access === true
      ) || false;
     const downloadAccess =
      permissionsGeneralAccess?.some(
        perm =>
          perm.feature === "Download" &&
          perm.access === true
      ) || false;
      const backdatedEntry =
      permissionsGeneralAccess?.some(
        perm =>
          perm.feature === "Backdated Entry" &&
          perm.access === true
      ) || false;
    setHasHyperfindAccess(hyperfindAccess);
    setHasDownloadAccess(downloadAccess);
    setBackDatedEntry(backdatedEntry);
}, [permissionsGeneralAccess]);
useEffect(() => {
    const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
    const storedFormValues = sessionStorage.getItem(FILTER_FORM_STORAGE_KEY);
    if (!stored || !storedFormValues) return;
     try {
      const values = JSON.parse(storedFormValues);
      filterForm.setFieldsValue(values);
      const filters = JSON.parse(stored);
      if (!filters || !Object.keys(filters).length) return;
      setAppliedFilters(filters);
      applyCrewFilters(filters, selectedDate);
    } catch (err) {
      console.error("Failed to restore crew filters", err);
    }
  }, []);
const fetchCrewsForDate = async (date, viewType = crewView) => {
    try {
      if (!canViewCrew) {
        message.error("You do not have permission to view crews.");
        return;
      }
      const effectiveViewType = (crewViewAccess === "Myself" || crewViewAccess === "None") ? 'myCrews' : viewType;
      const payload = {
        date: date ? dayjs(date).format("YYYY-MM-DD") : undefined,
        ...(effectiveViewType === 'myCrews' && { userId: userConfig?.userid }),
        page: 1,
        filters: sessionStorage.getItem(FILTER_STORAGE_KEY) ? appliedFilters : { isLayoffCrew: false }
     };
      const res = await getCrewAll(payload);
      const list = res?.data || [];
      const sortedList = await applyDefaultCrewSort(list);
      setCrews(sortedList);
      setIgnoreDateFilter(false);
      setFiltersApplied(false);
    } catch (err) {
      console.error("fetchCrewsForDate", err);
      message.error("Failed to fetch crew data");
    }
  };

const handleAiCrewCreated = async ({ date } = {}) => {
  const parsedDate = date ? dayjs(date) : dayjs(selectedDate);
  const targetDate = parsedDate.isValid() ? parsedDate.startOf('day') : dayjs(selectedDate);

  // "View in Crew Dashboard" is an explicit navigation action. Do not leave the newly
  // created crew hidden behind a prior date, an "All Crews" selection, or saved filters.
  setSelectedDate(targetDate);
  setCrewView('myCrews');
  setAppliedFilters(null);
  setShowFilterDot(false);
  setFiltersApplied(false);
  filterForm.resetFields();
  try {
    sessionStorage.setItem(SHARED_SELECTED_DATE_STORAGE_KEY, targetDate.toISOString());
    sessionStorage.setItem(CREW_VIEW_STORAGE_KEY, 'myCrews');
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
    sessionStorage.removeItem(FILTER_FORM_STORAGE_KEY);
  } catch (error) {
    // Storage is only a convenience; the in-memory state above is authoritative.
  }
  await fetchCrewsForDate(targetDate, 'myCrews');
};
const getRowKey = (record) => record._id || record.key || record.id;
const applyCrewFilters = async (filters, date, options = {}) => {
    const { showSuccess = false, closeModal = false, } = options;
    const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
    const storedFormValues = sessionStorage.getItem(FILTER_FORM_STORAGE_KEY);
    if (!stored || !storedFormValues) return;
try {
      setLoading(true);
      setShowFilterDot(true);
      const params = {
        date: date ? dayjs(date).format("YYYY-MM-DD") : undefined,
        ...(crewView === "myCrews" && { userId: userConfig?.userid }),
        page: 1,
        filters: Object.keys(filters || {}).length ? filters : undefined,
      };
     const res = await getCrewAll(params);
     const successFlag = res?.data?.success === true;
      const dataArray =
        Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
      const sortedData = await applyDefaultCrewSort(dataArray);
       if (successFlag && sortedData) {
        setCrews(sortedData);
        setFiltersApplied(true);
        setIgnoreDateFilter(true);
        setIsFilterModalOpen(false);
        message.success("Crew data filtered successfully!");
      } else if (sortedData && sortedData.length > 0) {
        setCrews(sortedData);
        setFiltersApplied(true);
        setIgnoreDateFilter(true);
        setIsFilterModalOpen(false);
      } else {
        message.warning("No data found for selected filters!");
      }
    if (!sortedData.length) {
        setCrews([]);
        if (closeModal) {
          setIsFilterModalOpen(false);
        }
        return;
      }
      setCrews(sortedData);
      setFiltersApplied(true);
      setIgnoreDateFilter(true);
      setAppliedFilters(filters);
      if (showSuccess) {
        message.success("Crew data filtered successfully!");
      }
     if (closeModal) {
        setIsFilterModalOpen(false);
      }
    } catch (err) {
      message.error("Failed to apply crew filters");
    } finally {
      setLoading(false);
    }
  };
const applyFilters = async () => {
    try {
      if (!canViewCrew) {
        message.error("You do not have permission to view crews.");
        return;
      }
      const values = await filterForm.validateFields();
      sessionStorage.setItem(FILTER_FORM_STORAGE_KEY, JSON.stringify(values));
      setShowFilterDot(true);
      setLoading(true);
      const raw = values || {};
      const filters = {};
       Object.entries(raw).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (Array.isArray(v)) {
          if (v.length === 0) return;
          filters[k] = v;
          return;
        }
        if (typeof v === 'string') {
          const t = v.trim();
          if (t === '') return;
          filters[k] = t;
          return;
        }
        filters[k] = v;
      });
    if (filters.startTime) {
        const timeOnly = filters.startTime;
        const dateStr = selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
        filters.startTime = dayjs(filters?.startTime).format('HH:mm');
      }
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
      setAppliedFilters(filters);
      await applyCrewFilters(filters, selectedDate, { showSuccess: true, closeModal: true, });
    } catch (err) {
      console.error("applyFilters", err);
      setLoading(false);
      const serverMsg = err?.response?.data?.message || err?.message;
      message.error(serverMsg || "Failed to apply filters");
    }
  };
 const pasteCopiedCrews = async () => {
    try {
      setLoading(true);
      const values = await copyForm.validateFields();
      const copyDate = values?.copyDate;
       if (!selectedRows?.length) {
        message.warning("Please select at least one crew");
        return;
      }
      const crewIds = selectedRows.map(row => row._id);
      const copyRes = await copyCrews({
        date: dayjs(copyDate).format("YYYY-MM-DD"),
        crewIds,
      });
      const response = copyRes?.data ?? copyRes;
      const hasConflicts = response?.results?.some(
        (r) => Array.isArray(r.conflictingEmployees) && r.conflictingEmployees.length > 0
      );
     if (hasConflicts) {
        setConflictResponse(response);
        setTheCrew(selectedRows);
      }
    const ukgPayload =
        copyRes?.data?.results?.flatMap(result =>
          (result.createdAssignmentsDetails || []).map(emp => ({
            id: emp?.employeeId || emp?.personNumber,
            workRuleTransfer: emp?.workRuleTransfer,
            payscale: emp?.payScale ?? emp?.payscale,
            wbsIo: emp?.event,
            task: emp?.task,
            premium: emp?.premium,
            scheduleIn: emp?.start,
            scheduleOut: emp?.end,
            assignDate: emp?.date,
            assignedAt: new Date(emp?.assignedAt).toISOString(),
          }))
        ) || [];
    if (ukgPayload.length) {
        await CrewScheduleWorkmen(ukgPayload);
      }
    setCopyBuffer([]);
      copyForm.resetFields();
      setIsCopyModalOpen(false);
      message.success(`Crew${selectedRows.length > 1 ? "s" : ""} copied successfully`);
      const targetDate = dayjs(copyDate).startOf("day");
      setSelectedDate(targetDate);
      setSelectedRows([]);
      try {
        sessionStorage.setItem(SHARED_SELECTED_DATE_STORAGE_KEY, targetDate.format("YYYY-MM-DD"));
      } catch (err) {
        console.error("Failed to save selected date", err);
      }
     await fetchCrewsForDate(targetDate);
} catch (err) {
      console.error("pasteCopiedCrews", err);
      message.error("Failed to copy crews");
    } finally {
      setLoading(false);
    }
  };
const deleteSelectedCrews = async () => {
    if (!selectedRows || selectedRows.length === 0) {
      message.warning("Please select at least one crew to delete!");
      return;
    }
    const allowedPermissionRows = selectedRows.filter((row) => canDeleteRow(row));
    const deniedPermissionRows = selectedRows.filter((row) => !canDeleteRow(row));
     if (allowedPermissionRows.length === 0) {
      message.error("You do not have permission to delete the selected crews.");
      return;
    }
   const lockedCrews = allowedPermissionRows.filter(
      (row) => row.employeesScheduled > 0
    );
   const deletableCrews = allowedPermissionRows.filter(
      (row) => row.employeesScheduled === 0
    );
    if (lockedCrews.length > 0) {
      const crewNames = lockedCrews.map((r) => r.crewName).join(", ");
     message.warning({
        content: (
          <span>
            Cannot delete selected crew(s) because employees are still assigned:{" "}
            <b>{crewNames}</b>
          </span>
        ),
        duration: 5,
      });
    }
   if (deletableCrews.length === 0) {
      return;
    }
 try {
      const payload = {
        ids: deletableCrews.map((row) => row._id),
      };
    await deleteCrew(payload);
    message.success("Selected crews deleted successfully!");
     if (deniedPermissionRows.length > 0) {
        message.warning(
          "Some crews were not deleted due to insufficient permissions."
        );
      }
      fetchCrewsForDate(selectedDate);
      setSelectedRowKeys([]);
      setSelectedRows([]);
    } catch (err) {
      console.error("deleteSelectedCrews error", err);
      message.error("Failed to delete selected crews");
    }
  };
const submitTransfer = async () => {
    try {
      if (!transferOwner) {
        message.warning("Select a crew owner before transferring");
        return;
      }
    setLoading(true);
      const owner = workmen?.find((emp) => emp?.userid === transferOwner);
      const ownerLabel = transferOwnerName || (owner ? `${owner?.lastName || ""}, ${owner?.firstName || ""}`.trim() : undefined);
      const payload = {
        crews: selectedRows.map(row => ({
          crewId: row._id,
          crewDetailsUpdates: {
            crewOwner: transferOwner,
            crewOwnerId: transferOwner,
          },
          employeeScheduleUpdates: [],
        })),
      };
     await updateCrewAndEmployeeDetails(payload);
     message.success("Crew owner transferred successfully");
      setIsTransferModalOpen(false);
      setTransferOwner(undefined);
      setTransferOwnerName(undefined);
      setSelectedRows([]);
      await fetchCrewsForDate(selectedDate);
    } catch (err) {
      console.error("submitTransfer", err);
      message.error("Failed to transfer crew owner");
    } finally {
      setLoading(false);
    }
  };
  const handleRowClick = async (record, event) => {
    if (event.target.nodeName === "INPUT" || event.target.className?.includes("ant-checkbox")) return;
    const rowKey = getRowKey(record);
    setSelectedRowKeys([rowKey]);
    setSelectedRows([record]);
    setCrewClickedDate(selectedDate);
    setIsUnassignModalOpen(true);
    RemoveFilters(userConfig)
    try {
      const res = await getCrew(record._id, true);
      const crew = res?.data?.crew ?? null;
      const schedule = res?.data?.schedule ?? [];
      setSelectedCrew(crew);
      setScheduledEmployees(schedule);
      // navigate("/home/supervisor/crewManagement/unassignDashboard");
     } catch (err) {
      console.error("handleRowClick getCrew", err);
      message.error("Failed to fetch crew details");
    }
  };
// menu actions
  const menuItems = [
    {
      key: "1",
      icon: <CopyOutlined />,
      disabled: selectedRows?.length === 0 || !copyCrewAceess,
      label: "Copy Crew",
      onClick: () => {
        setCopyBuffer(selectedRows);
        setIsCopyModalOpen(true);
      },
    },
    {
      key: "2",
      icon: <SwapOutlined />,
      disabled: selectedRows?.length === 0 || !transferCrewAcess,
      label: "Transfer Crew Owner",
      onClick: () => {
        setIsTransferModalOpen(true);
        fetchEmployeeList();
      },
    },
    {
      key: "3",
      icon: <EditOutlined />,
      disabled: !CrewTemplateManagementAcess,
      label: <Link to={`/home/supervisor/crewManagement/crewTemplateEdit`}>Crew Template</Link>,
    },
    {
      key: "4",
      icon: <DeleteOutlined />,
      danger: true,
      disabled: selectedRows?.length === 0,
      label: <span style={{ paddingLeft: 6 }}>Delete</span>,
      onClick: () => {
        Modal.confirm({
          title: "Are you sure?",
          okText: "Yes",
          cancelText: "No",
          onOk: deleteSelectedCrews,
        });
      },
    },
  ];
const handleDateChange = (d) => {
    if (!d) setCrews([]);
    setSelectedRows([]);
    setSelectedRowKeys([]);
    const isToday = d && d.isSame(dayjs(), "day");
    if (isToday) {
      const newDate = dayjs(d.toDate()).startOf("day");
      setSelectedDate(newDate);
      try {
        sessionStorage.setItem(SHARED_SELECTED_DATE_STORAGE_KEY, newDate.format("YYYY-MM-DD"));
      } catch (err) {
        console.error("Failed to save selected date", err);
      }
    } else {
      const newDate = d?.startOf("day");
      setSelectedDate(newDate);
      try {
        sessionStorage.setItem(SHARED_SELECTED_DATE_STORAGE_KEY, newDate.format("YYYY-MM-DD"));
      } catch (err) {
        console.error("Failed to save selected date", err);
      }
    }
    if (appliedFilters) applyCrewFilters(appliedFilters, d);

  };
const handleResetFilters = () => {
    filterForm.resetFields();
    setFiltersApplied(false);
    setIgnoreDateFilter(false);
    setIsApplyDisabled(true);
    setShowFilterDot(false);
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
    sessionStorage.removeItem(FILTER_FORM_STORAGE_KEY);
try {
      fetchCrewsForDate(selectedDate);
    } catch (err) {
      console.error('Error refetching employee list after reset:', err);
    }
  };
const clearScheduleStatusFilters = () => {
    if (scheduleStatusFiltersRef.current?.clearAllFilters) {
      scheduleStatusFiltersRef.current.clearAllFilters();
    }
  };
const handleResetColumnFilters = () => {
    if (tableFiltersRef.current?.clearAllFilters) {
      tableFiltersRef.current.clearAllFilters();
    }
    clearScheduleStatusFilters();
    message.success('Column filters cleared');
  };
const downloadCrewList = () => {
    const dataToDownload =
      visibleCrews?.length > 0 ? visibleCrews : crews;
   if (!dataToDownload || dataToDownload.length === 0) {
      message.warning("No crew data available to download");
      return;
    }
    const columns = getEmployeeExcelColumns();
    const excelData = dataToDownload.map((crew, index) => {
      const row = { "S.No": index + 1 };
     columns.forEach((col) => {
        let value = "";
    if (col.key === "difference") {
          const needed = Number(crew?.employeesNeeded) || 0;
          const scheduled = Number(crew?.employeesScheduled) || 0;
          value = scheduled - needed;
        } else {
          const rawValue = crew?.[col.key];
      if (rawValue && typeof rawValue === "object") {
            value =
              rawValue.label ||
              rawValue.name ||
              rawValue.value ||
              rawValue.title ||
              "";
          } else {
            value = rawValue ?? "";
          }
        }
const headerName =
          typeof col.header === "object"
            ? col.header?.title ||
            col.header?.label ||
            col.key
            : col.header;
row[headerName] = value;
      });

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    if (excelData.length > 0) {
      worksheet["!cols"] = Object.keys(excelData[0]).map((key) => ({
        wch:
          Math.max(
            key.length,
            ...excelData.map((row) =>
              String(row[key] ?? "").length
            )
          ) + 2,
      }));
    }
const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Crew List"
    );
const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileData = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(
      fileData,
      `Crew_List_${dayjs(selectedDate).format("YYYY-MM-DD")}.xlsx`
    );
  };
const refreshDataHandler = async (refreshParameter) => {
    refreshData({ refreshParameter })
      .then((response) => {
        message.success("Refreshed successfully");
        fetchCrewsForDate(selectedDate);
      })
      .catch((err) => {
        console.error("fetchCrewsFromFLS", err);
        message.error("Failed to fetch crew data from FLS");
      });
  }
const refreshMenuItems = [
    {
      key: "labourCategories",
      label: "Refresh labor categories",
      onClick: () => refreshDataHandler("labourCategories"),
    },
    {
      key: "employeeDetails",
      label: "Refresh employees",
      onClick: () => refreshDataHandler("employeeDetails"),
    },
    {
      key: "flsData",
      label: "Refresh crews from FLS",
      onClick: () => refreshDataHandler("flsData"),
    },
  ];
const handleCreateCrew = () => {
const today = dayjs().startOf('day');
const selectedDatee = dayjs(selectedDate).startOf('day');
  if (!backDatedEntry && selectedDatee.isBefore(today)) {
      message.error(
        'Crew creation is not allowed for past dates.'
      );
      return;
    }
setIsCreateModalOpen(true);
RemoveFilters(userConfig)
  };
return (
    <div style={{ background: "#fff", padding: "12px", borderRadius: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: "-20px", marginTop: "-20px" }}>
        <Col>
          <h2 style={{ margin: 0 }}>Crew Management</h2>
        </Col>
        <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Tooltip title="Download Crews">
            <Button onClick={downloadCrewList} icon={<DownloadOutlined />} disabled={!hasDownloadAccess} />
          </Tooltip>
      <Dropdown menu={{ items: refreshMenuItems }} trigger={['click']} placement="bottomRight">
            <Button icon={<ReloadOutlined />}>
              Refresh <DownOutlined />
            </Button>
          </Dropdown>

          <Tooltip title="Reset column filters">
            <Button onClick={handleResetColumnFilters} icon={<RedoOutlined />} />
          </Tooltip>

          <Badge dot={showFilterDot} color="#1890ff" offset={[-1, 1]} style={{ height: '10px', width: '10px' }}>
            <Tooltip title="Filters">
              <Button onClick={() => setIsFilterModalOpen(true)} icon={<FilterOutlined />} style={{ border: "1px solid #91d5ff", backgroundColor: "white", color: "#1890ff" }} disabled={!filterCrewAcess} />
            </Tooltip>
          </Badge>
        </Col>
      </Row>
    <Divider style={{ borderColor: '#ccc' }} />
    <Row align="middle" justify="space-between" style={{ marginBottom: "10px", flexWrap: "wrap", gap: 12, marginTop: "-10px" }}>
      <Col style={{ display: "flex", flexDirection: "column" }}>
          <label style={{ fontSize: 12, color: "#555", marginBottom: 4, fontWeight: 800 }}>Date</label>
       <StrictDatePicker value={selectedDate} onChange={handleDateChange} style={{ width: 140 }} allowClear={false} />
        </Col>
    <Col flex="auto" style={{ display: "flex", justifyContent: "center" }}>
          <Row gutter={[12, 12]} justify="center" style={{ textAlign: "center", marginBottom: '-10px' }}>
            <Col>
              <div style={{ backgroundColor: "#E3F8FF", borderRadius: 6, padding: "5px 8px", minWidth: 130 }}>
                <span>Total Needed : </span>
                <strong>{totalNeeded}</strong>
              </div>
            </Col>
            <Col>
              <div style={{ backgroundColor: "#E3F8FF", borderRadius: 6, padding: "5px 8px", minWidth: 160 }}>
                <span>Total Scheduled : </span>
                <strong>{totalScheduled}</strong>
              </div>
            </Col>
            <Col>
              <div style={{ backgroundColor: "#E3F8FF", borderRadius: 6, padding: "5px 8px", minWidth: 100 }}>
                <span>Total +/- : </span>
                <strong>{totalDiff}</strong>
              </div>
            </Col>
          </Row>
        </Col>
      <Col style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: '-15px' }}>
          <Select
            value={crewView}
            onChange={(value) => {
              setCrewView(value);
              try {
                sessionStorage.setItem(CREW_VIEW_STORAGE_KEY, value);
              } catch (e) {
              }
              fetchCrewsForDate(selectedDate, value);
            }}
            style={{ width: 120 }}
            disabled={!canSwitchCrewView}
          >
            <Option value="myCrews">My Crews</Option>
            <Option value="allCrews">All Crews</Option>
          </Select>

          <Button type="primary" onClick={handleCreateCrew} disabled={!canCreateCrew}>
            Create Crew
          </Button>
          <AiCrewEntry disabled={!canCreateCrew} selectedDate={selectedDate} licensed={Boolean(tokenInfo?.includes('crewcall'))}
            onCrewCreated={handleAiCrewCreated} onFallbackToManualWizard={handleCreateCrew} />
          <Tooltip title="More Options">
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <div style={{ border: "1px solid #91d5ff", borderRadius: 6, padding: "4px 8px", backgroundColor: "white", cursor: "pointer" }}>
              <MoreOutlined style={{ fontSize: 20, color: "#1890ff" }} />
            </div>
          </Dropdown>
          </Tooltip>
        </Col>
      </Row>

      <SmartTable
        tableKey="crew-dashboard-table"
        className="table-style"
        columns={crewColumns}
        loading={loading}
        dataSource={crewsWithDifference.map(row => ({ ...row, key: getRowKey(row) }))}
        pagination={{
          pageSize: tablePageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={(pagination, filters, sorter, extra) => {
          if (pagination && pagination.pageSize) {
            setTablePageSize(pagination.pageSize);
            const key = userConfig?.userid ? `employee-table-page-size-${userConfig.userid}` : null;
            if (key && typeof window !== "undefined") {
              window.localStorage.setItem(key, String(pagination.pageSize));
            }
          }
          if (pagination && pagination.current) setTableCurrentPage(pagination.current);
          if (extra && extra.currentDataSource) {
            setVisibleCrews(extra.currentDataSource);
          }
        }}
        onFiltersReady={(methods) => { tableFiltersRef.current = methods; }}
        onFilteredDataChange={setVisibleCrews}
        scroll={crewsWithDifference?.length ? { x: "100%", y: 455 } : { y: 455 }}
        rowSelection={{
          type: "checkbox",
          fixed: 'left',
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        bordered={true}
        size="middle"
        style={{ background: "#E2E8F0" }}
        rowClassName={() => "crew-table-row"}
        onRow={(record) => ({
          onClick: (event) =>
            handleRowClick(record, event),
        })}
        rowKey={getRowKey}
      />
   <style>{`.crew-table-row td { vertical-align: middle !important; } .ant-table-thead > tr > th { background: #EAEEFF !important; }`}</style>
   {/* Create Crew modal */}
      <Modal open={isCreateModalOpen} onCancel={() => { stepperResetRef.current?.(); setIsCreateModalOpen(false); }} footer={null} width="100%" centered maskClosable={false} keyboard={false} style={{ right: 10, left: 10 }}>
        <div style={{ padding: 32, background: "#fff", borderRadius: 16, minHeight: 600 }}>
          <MainStepper selectedDate={selectedDate} onClose={() => { setIsCreateModalOpen(false); fetchCrewsForDate(selectedDate); }} templateAccess={templatePermissions} scheduleOverridePermission={scheduleOverridePermission} onReset={(fn) => (stepperResetRef.current = fn)} />
        </div>
      </Modal>
 {/* Unassign / Crew details modal */}
      <Modal open={isUnassignModalOpen} onCancel={() => { const dateToUse = crewClickedDate || selectedDate; setIsUnassignModalOpen(false); setCrewClickedDate(null); setSelectedDate(dateToUse); fetchCrewsForDate(dateToUse); }} footer={null} width={1250} centered maskClosable={false} keyboard={false} style={{ right: 10, left: 10 }}>
        <div style={{ padding: 32, background: "#fff", borderRadius: 16, minHeight: 600 }}>
          {selectedCrew && <UnassignDashboard key={`${selectedCrew._id}-${Date.now()}`} basicDetails={selectedCrew} employees={scheduledEmployees || []} assignDate={crewClickedDate || selectedDate} onClose={(dateToRefresh) => { const dateToUse = dateToRefresh || crewClickedDate || selectedDate; setIsUnassignModalOpen(false); setCrewClickedDate(null); setSelectedDate(dateToUse); fetchCrewsForDate(dateToUse); }} onRefresh={async () => { try { const res = await getCrew(selectedCrew._id, true); const updatedCrew = res?.data?.crew ?? null; const updatedSchedule = res?.data?.schedule ?? []; if (updatedCrew) { setSelectedCrew(updatedCrew); setScheduledEmployees(updatedSchedule); } } catch (err) { console.error("onRefresh getCrew", err); } }} scheduleOverridePermission={scheduleOverridePermission} />}
        </div>
      </Modal>
{/* Transfer modal */}
      <Modal open={isTransferModalOpen} onCancel={() => setIsTransferModalOpen(false)} footer={null} width={400} centered maskClosable={false} keyboard={false}>
        <Title level={4} style={{ textAlign: "center", marginBottom: 8, fontWeight: 500 }}>Transfer Crew Owner</Title>
        <Divider style={{ margin: "8px 0 20px 0" }} />
<Select
          showSearch
          placeholder="Select Crew Owner"
          optionFilterProp="children"
          style={{ width: "80%", marginBottom: 20 }}
          value={transferOwner ?? undefined}
          onChange={(v, option) => {
            setTransferOwner(v);
            setTransferOwnerName(option?.children);
          }}
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {(workmen || [])
            .filter((emp) => {
              const id = emp?.userid;
              const excludeIds = (selectedRows || [])
                .map((r) => r?.crewOwnerId)
                .filter(Boolean);
              return !excludeIds.includes(id);
            })
            .map((emp) => (
              <Option key={emp?.id} value={emp?.userid}>
                {emp?.lastName + ", " + emp?.firstName}
              </Option>
            ))}
        </Select>
        <Button type="primary" block onClick={submitTransfer}>Submit</Button>
      </Modal>
{/* Filter modal */}
      <Modal open={isFilterModalOpen} onCancel={() => { setIsFilterModalOpen(false) }} footer={null} width={400} centered maskClosable={false} keyboard={false} closable={false}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600, fontSize: 18 }}>Crew Filter</Title>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <a onClick={handleResetFilters} style={{ color: "#1677ff", fontWeight: 500, cursor: "pointer", fontSize: 14 }}>Reset</a>
            <CloseOutlined onClick={() => { setIsFilterModalOpen(false); }} style={{ fontSize: 16, cursor: "pointer", color: "#333" }} />
          </div>
        </div>
    <Divider style={{ margin: "10px 0 20px 0" }} />
<Form
          layout="vertical"
          form={filterForm}
          style={{ marginTop: 8 }}
          onValuesChange={() => {
            try {
              const values = filterForm.getFieldsValue();
              const isEmpty = Object.values(values).every((v) => {
                if (v === undefined || v === null) return true;
                if (Array.isArray(v)) return v.length === 0;
                if (typeof v === "string") return v.trim() === "";
                if (dayjs.isDayjs && dayjs.isDayjs(v)) return false;
                if (typeof v === "object") return Object.keys(v).length === 0;
                return false;
              });
              setIsApplyDisabled(isEmpty);
              setShowFilterDot(false);
            } catch (e) {
              setIsApplyDisabled(true);
            }
          }}
        >
          <Form.Item label="Crew Name" name="crewName">
            <Select placeholder="Select Crew" showSearch allowClear mode="multiple" >
              {[...new Set(crewsWithDifference.map((c) => c.crewName).filter(name => name && String(name).trim() !== ""))]
                .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
                .map((name) => <Option key={name} value={name}>{name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item label="Crew Lead" name="crewLead" >
            <Select showSearch allowClear placeholder="Select Crew Lead" mode="multiple" >
              {[...new Set(crewsWithDifference.map((c) => c.crewLead).filter(name => name && String(name).trim() !== ""))]
                .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
                .map((lead) => <Option key={lead} value={lead}>{lead}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item label="Task" name="task" mode="multiple" >
           <Select 
              showSearch 
              allowClear 
              placeholder="Select Task" 
              mode="multiple" 
              filterOption={(input, option) =>
                (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
            >
              {[...new Set(crewsWithDifference.map((c) => c.task).filter(name => name && String(name).trim() !== ""))]
                .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
                .map((lead) => <Option key={lead} value={lead}>{lead}</Option>)}
            </Select>
          </Form.Item>
      <Form.Item label="Event" name="eventName" >
         <Select showSearch allowClear placeholder="Select Event Name" mode="multiple" >
              {[...new Set(crewsWithDifference.map((c) => c.eventName).filter(name => name && String(name).trim() !== ""))]
                .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
                .map((lead) => <Option key={lead} value={lead}>{lead}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item label="Start time" name="startTime">
            <TimePicker
              format="HH:mm"
              style={{ width: "100%", borderRadius: 6 }}
              placeholder="Select time (24hr)"
              allowClear
              needConfirm={false}
              open={startTimeOpen}
              onOpenChange={setStartTimeOpen}
              onChange={(val) => {
                filterForm.setFieldsValue({ startTime: val });
                setStartTimeOpen(false);
                updateApplyDisabledState();
              }}
              onBlur={(e) => {
                const parsed = parseTimeInputValue(e?.target?.value);
                if (parsed) {
                  filterForm.setFieldsValue({ startTime: parsed });
                  updateApplyDisabledState();
                }
              }}
              onClear={() => {
                filterForm.setFieldsValue({ startTime: null });
                updateApplyDisabledState();
              }}
              onKeyDown={(e) => {
                if (e.key === "Tab" || e.key === "Enter") {
                  const parsed = parseTimeInputValue(e?.target?.value);
                  if (parsed) {
                    filterForm.setFieldsValue({ startTime: parsed });
                    updateApplyDisabledState();
                  }
                }
                handleTimeInput(e, 'startTime', setStartTimeOpen);
              }}
            />
          </Form.Item>
        <Button
            type="primary"
            block
            onClick={applyFilters}
            disabled={loading || isApplyDisabled}
          >
            {loading ? <Spin size="small" /> : "Apply"}
          </Button>
        </Form>
      </Modal>
    {/* Copy modal */}
      <Modal open={isCopyModalOpen} onCancel={() => setIsCopyModalOpen(false)} footer={null} width={300} centered style={{ marginTop: "200px", marginLeft: "200px" }} maskClosable={false} keyboard={false}>
        <Title level={4} style={{ textAlign: "center", marginBottom: 8, fontWeight: 600, marginTop: "-10px" }}>Copy Crews</Title>
        <Divider style={{ margin: "8px 0 20px 0" }} />
        <Form form={copyForm} layout="vertical">
          <Form.Item label={<span style={{ fontWeight: 500 }}>Copy to Date</span>} name="copyDate" rules={[{ required: true, message: "Please select a date" }]} style={{ marginBottom: 20 }}>
            <StrictDatePicker
              style={{ width: "100%" }}
              placeholder="Select Date"
              popupClassName="custom-date-popup"
              disabledDate={(current) => {
                const today = dayjs().startOf('day');
                return current.startOf('day').isBefore(today)
              }}
            />
          </Form.Item>
          <Button
            type="primary"
            block
            disabled={loading}
            onClick={pasteCopiedCrews}
            style={{ height: 38, fontWeight: 500 }}
          >
            {loading ? <Spin size="small" /> : "Submit"}
          </Button>
      </Form>
      </Modal>
      <>
        {conflictResponse && (
          <ScheduleConflictHandler
            response={conflictResponse}
            crew={theCrew}
            onDone={() => {
              setConflictResponse(null);
              fetchCrewsForDate(selectedDate);
            }}
            scheduleOverridePermission={scheduleOverridePermission}
            onFiltersReady={(methods) => { scheduleStatusFiltersRef.current = methods; }}
            onCloseFilter={clearScheduleStatusFilters}
          />
        )}
      </>
    </div>
  );
}
export default CrewDashboard;
